import React, { useState, useCallback } from 'react';
import { RobotOutlined, EyeOutlined } from '@ant-design/icons';
import { InlineButtonLayout } from '../InlineButtonEditor';
import ChatBubble from './ChatBubble';
import './chatPreview.css';

export interface SubPage {
  pageIndex: number;
  title: string;
  replyType: 'text' | 'photo' | 'video';
  text: string;
  mediaUrl?: string | null;
  parseMode: 'HTML' | 'Markdown';
  buttons: InlineButtonLayout;
}

export interface TelegramChatPreviewProps {
  triggerText?: string;
  replyText?: string;
  replyType?: 'text' | 'photo' | 'video';
  mediaUrl?: string | null;
  parseMode?: 'HTML' | 'Markdown';
  buttons?: InlineButtonLayout;
  dynamicVarFields?: string[];
  subPages?: SubPage[];
  botName?: string;
}

const TelegramChatPreview: React.FC<TelegramChatPreviewProps> = ({
  triggerText,
  replyText,
  replyType = 'text',
  mediaUrl,
  parseMode = 'HTML',
  buttons,
  dynamicVarFields = [],
  subPages = [],
  botName = 'Bot',
}) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageHistory, setPageHistory] = useState<number[]>([]);
  const [switchedContent, setSwitchedContent] = useState<{ text: string; buttons?: InlineButtonLayout } | null>(null);

  const handleNavigate = useCallback((targetPageIndex: number) => {
    setPageHistory(prev => [...prev, currentPageIndex]);
    setCurrentPageIndex(targetPageIndex);
    setSwitchedContent(null);
  }, [currentPageIndex]);

  const handleSwitchContent = useCallback((altText: string, altButtons?: InlineButtonLayout) => {
    setSwitchedContent({ text: altText, buttons: altButtons });
  }, []);

  const handleBreadcrumbNav = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
    setSwitchedContent(null);
    if (pageIndex === 0) {
      setPageHistory([]);
    } else {
      const idx = pageHistory.indexOf(pageIndex);
      if (idx !== -1) {
        setPageHistory(prev => prev.slice(0, idx));
      }
    }
  };

  // Get current page data
  const getCurrentPageData = () => {
    if (currentPageIndex === 0) {
      return {
        text: replyText || '',
        replyType,
        mediaUrl,
        parseMode,
        buttons: buttons || [],
      };
    }
    const sub = subPages.find(p => p.pageIndex === currentPageIndex);
    if (!sub) return null;
    return {
      text: sub.text || '',
      replyType: sub.replyType,
      mediaUrl: sub.mediaUrl,
      parseMode: sub.parseMode,
      buttons: sub.buttons || [],
    };
  };

  const currentData = getCurrentPageData();

  const hasContent = currentPageIndex === 0
    ? (replyText || replyType !== 'text')
    : !!currentData;

  // Build breadcrumb: root + all pages in history + current
  const buildBreadcrumb = () => {
    const crumbs: Array<{ pageIndex: number; label: string }> = [
      { pageIndex: 0, label: '主页面' },
    ];
    pageHistory.forEach(idx => {
      if (idx === 0) return;
      const sub = subPages.find(p => p.pageIndex === idx);
      crumbs.push({ pageIndex: idx, label: sub?.title || `子页面 ${idx}` });
    });
    return crumbs;
  };

  const breadcrumbs = buildBreadcrumb();
  const currentLabel = currentPageIndex === 0
    ? '主页面'
    : (subPages.find(p => p.pageIndex === currentPageIndex)?.title || `子页面 ${currentPageIndex}`);

  const isMultiPage = subPages.length > 0;

  return (
    <div className="tg-preview-wrapper">
      {/* Header */}
      <div className="tg-preview-header">
        <div className="tg-preview-avatar">
          <RobotOutlined />
        </div>
        <div className="tg-preview-header-info">
          <div className="tg-preview-bot-name">{botName}</div>
          <div className="tg-preview-status">bot</div>
        </div>
        <span className="tg-preview-label">
          <EyeOutlined style={{ marginRight: 3 }} />
          实时预览
        </span>
      </div>

      {/* Breadcrumb navigation for multi-page */}
      {isMultiPage && (
        <div className="tg-preview-breadcrumb">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={crumb.pageIndex}>
              {i > 0 && <span className="tg-breadcrumb-sep">›</span>}
              <span
                className="tg-breadcrumb-item"
                onClick={() => handleBreadcrumbNav(crumb.pageIndex)}
              >
                {crumb.label}
              </span>
            </React.Fragment>
          ))}
          <span className="tg-breadcrumb-sep">›</span>
          <span className="tg-breadcrumb-item active">{currentLabel}</span>
        </div>
      )}

      {/* Messages */}
      <div className="tg-preview-messages">
        {!hasContent ? (
          <div className="tg-preview-empty">
            <RobotOutlined style={{ fontSize: 28, opacity: 0.3 }} />
            <span>填写回复内容后预览效果</span>
          </div>
        ) : (
          <>
            {/* Trigger bubble (only on root page) */}
            {currentPageIndex === 0 && triggerText && (
              <ChatBubble
                sender="user"
                text={triggerText}
                parseMode="HTML"
              />
            )}

            {/* Bot reply bubble */}
            {currentData && (
              <ChatBubble
                sender="bot"
                senderName={botName}
                text={currentData.text}
                displayText={switchedContent ? switchedContent.text : undefined}
                parseMode={currentData.parseMode as 'HTML' | 'Markdown'}
                mediaUrl={currentData.mediaUrl}
                mediaType={currentData.replyType !== 'text' ? (currentData.replyType as 'photo' | 'video') : undefined}
                buttons={currentData.buttons}
                displayButtons={switchedContent?.buttons}
                dynamicVarFields={dynamicVarFields}
                onNavigate={handleNavigate}
                onSwitchContent={handleSwitchContent}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TelegramChatPreview;
